import React, { useEffect, useRef, useState } from 'react';
import { Redirect, Link, useLocation, useHistory } from 'react-router-dom';
import { Alert, Button } from '@betareader/design-system';
import AppIcon from '../../components/AppIcon';
import queryString from 'query-string';
// i18n
import { useTranslation } from 'react-i18next';
// redux
import {useDispatch, useSelector} from '../../reduxHooks';
import { Media } from 'src/utils/Media';

import ReactGA from 'react-ga';

import SalesPitch from '../../components/standard-messages/salesPitch';
import { authUtil, userProfileUtil } from '../../utils';
import ForgotPasswordWidget from 'src/components/ForgotPasswordWidget';
import fire from 'src/config/fire';
import { getUserProfile } from 'src/modules/user';
import './auth.css';

// Staging's Firebase auth helper is on a different domain from the app.
// A popup avoids relying on cross-site redirect storage for social sign-in.
const useSocialPopup =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_FIREBASE_ENV === 'staging';

const SignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userProfile = useSelector(state => state.user.userProfile);
  const user = useSelector(state => state.user.user);
  const profileLoadError = useSelector(state => state.user.profileLoadError);

  const history = useHistory();
  const location = useLocation();

  const emailInput = useRef<HTMLElement | null>(null);
  const passwordInput = useRef<HTMLElement | null>(null);
  const [redirect, setRedirect] = useState<string | null>(null);
  const [show, setShow] = useState('stage1');
  const [stateUser, setStateUser] = useState(null);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<null | string | undefined>(
    null
  );
  const [message, setMessage] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [verifyingMagicLink, setVerifyingMagicLink] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState<false | 'magic-link' | 'password'>(false);

  const [token, setToken] = useState(null);

  const getToken = async () => {
    const _token = await authUtil.getFreshIdToken();

    setToken(_token);
  };

  useEffect(() => {
    console.log('getting token');

    getToken();
  }, []);

  // handle first mount
  useEffect(() => {
    const handleFirstLoad = async () => {
      ReactGA.pageview(location.pathname + location.hash + location.search);

      const {
        redirect,
        invitationToken: refererToken,
        ur: userRole,
      } = queryString.parse(location.search.replace('?', ''));
      if (refererToken) {
        localStorage.setItem('br-signup-referer-token', refererToken);
      }
      if (userRole) {
        localStorage.setItem('br-signup-user-role', userRole);
      }

      setRedirect(redirect);
      setShow('stage1');
      const signInMode = localStorage.getItem('sign-in-mode');
      if (signInMode !== null && ['redirect', 'popup'].includes(signInMode)) {
        localStorage.removeItem('sign-in-mode');
        setSigningIn(true);
      }

      const isSignInWithEmailLink = fire
        .auth()
        .isSignInWithEmailLink(window.location.href);
      if (isSignInWithEmailLink) {
        const _email = window.localStorage.getItem('emailForSignIn');
        if (!_email) {
          // User opened the link on a different device. To prevent session fixation
          // attacks, ask the user to provide the associated email again.
          setShow('stage3');
        } else {
          setEmail(_email);
          setVerifyingMagicLink(true);
        }
      }

      try {
        if (emailInput.current) {
          emailInput.current.querySelector<HTMLInputElement>('input')?.focus();
        }

        const redirectResult = await fire.auth().getRedirectResult();

        const { user: _user } = redirectResult;
        setStateUser(_user);

        if (redirectResult.credential) {
          // This gives you a Google Access Token.
          const credential = redirectResult.credential;
        }

        if (!redirectResult.user) {
          // show sign in
          setShow('stage1');
        }
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : String(error));
      } finally {
        localStorage.removeItem('sign-in-mode');
        setSigningIn(false);
      }
    };

    handleFirstLoad();
  }, []);

  useEffect(() => {
    if (verifyingMagicLink) {
      signInWithMagicLink();
    }
  }, [verifyingMagicLink]);

  useEffect(() => {
    if (passwordInput.current && show === 'stage2') {
      // if the password input box just appeared
      try {
        passwordInput.current.querySelector<HTMLInputElement>('input')?.focus();
      } catch (e) {
        // do nothing
      }
    }
  }, [show]);

  const passwordChanged = (event: any, data: any) => {
    setPassword(data.value);
    setErrorMessage(undefined);
  };

  const emailChanged = (event: any) => {
    const newEmail = event.currentTarget.value;
    setEmail(newEmail);
    setErrorMessage(undefined);
  };

  const signIn = async (type: 'email' | 'apple' | 'google' | 'facebook') => {
    const appleAuthProvider = new fire.auth.OAuthProvider('apple.com');
    const googleAuthProvider = new fire.auth.GoogleAuthProvider();
    const facebookAuthProvider = new fire.auth.FacebookAuthProvider();

    try {
      switch (type) {
        case 'email':
          await fire.auth().signInWithEmailAndPassword(email, password);
          break;
        case 'apple':
          if (useSocialPopup) {
            setSigningIn(true);
            try {
              await fire.auth().signInWithPopup(appleAuthProvider);
            } finally {
              setSigningIn(false);
            }
          } else {
            localStorage.setItem('sign-in-mode', 'redirect');
            await fire.auth().signInWithRedirect(appleAuthProvider);
          }
          break;
        case 'google':
          if (useSocialPopup) {
            setSigningIn(true);
            try {
              await fire.auth().signInWithPopup(googleAuthProvider);
            } finally {
              setSigningIn(false);
            }
          } else {
            localStorage.setItem('sign-in-mode', 'redirect');
            await fire.auth().signInWithRedirect(googleAuthProvider);
          }
          break;
        case 'facebook':
          if (useSocialPopup) {
            setSigningIn(true);
            try {
              await fire.auth().signInWithPopup(facebookAuthProvider);
            } finally {
              setSigningIn(false);
            }
          } else {
            localStorage.setItem('sign-in-mode', 'redirect');
            await fire.auth().signInWithRedirect(facebookAuthProvider);
          }
          break;
        default:
      }
    } catch (error) {
      localStorage.removeItem('sign-in-mode');
      setSigningIn(false);
      setErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  const handleEnterEmail = () => {
    if (userProfileUtil.validateEmail(email)) {
      setShow('stage2');
    }
  };

  const handleEnterPassword = () => {
    signIn('email');
  };

  const handleUseMagicLink = () => {
    setLoading('magic-link');
  };

  useEffect(() => {
    const handleLoadingChange = async () => {
      try {
        const actionCodeSettings = {
          // URL you want to redirect back to. The domain (www.example.com) for this
          // URL must be whitelisted in the fire Console.
          url: window.location.href,
          // This must be true.
          handleCodeInApp: true,
        };

        await fire.auth().sendSignInLinkToEmail(email, actionCodeSettings);
        // The link was successfully sent. Inform the user.
        // Save the email locally so you don't need to ask the user for it again
        // if they open the link on the same device.
        window.localStorage.setItem('emailForSignIn', email);
        setMessage(String(t('LinkWasSentToX', { x: email })));
      } catch (error) {
        // Some error occurred, you can inspect the code: error.code
        // const errorCode = error.code;
        setErrorMessage(error instanceof Error ? error.message : String(error));
      } finally {
        setLoading(false);
      }
    };

    if (loading === 'magic-link') {
      handleLoadingChange();
    }
  }, [loading]);

  const signInWithMagicLink = async () => {
    try {
      await fire.auth().signInWithEmailLink(email, window.location.href);
      // Clear email from storage.
      window.localStorage.removeItem('emailForSignIn');
      // You can access the new user via result.user
      // Additional user info profile not available via:
      // result.additionalUserInfo.profile == null
      // You can check if the user is new or existing:
      // result.additionalUserInfo.isNewUser
    } catch (error) {
      // ignore
    }
  };

  if (user !== undefined && userProfile !== undefined) {
    return (
      <Redirect
        to={
          redirect ||
          (['WRITER', 'BOTH'].includes(userProfile.role)
            ? '/mymanuscripts'
            : '/discover')
        }
      />
    );
  }

  const resetEverything = async () => {
    // reset everything locally
    setErrorMessage(undefined);
    setMessage(null);
    setSigningIn(false);
    setVerifyingMagicLink(false);
    setEmail('');
    setPassword('');
    setLoading(false);
    setShow('stage1');

    // reset firebase
    try {
      await fire.auth().signOut();
    } catch (error) {
      // ignore
    }
  };

  const retryProfile = async () => {
    try {
      const idToken = await authUtil.getFreshIdToken();
      if (idToken) {
        dispatch(getUserProfile(idToken));
      } else {
        setErrorMessage('Your sign-in session has expired. Please sign in again.');
      }
    } catch (error) {
      setErrorMessage('Could not refresh your sign-in session. Please sign in again.');
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (show === 'stage1') handleEnterEmail();
    else if (show === 'stage2') handleEnterPassword();
    else if (show === 'stage3') signInWithMagicLink();
  };

  return (
    <main className="br-auth-page">
      <img className="br-auth-logo" src="/assets/images/BR-orange-horizontal.png" alt="BetaReader" />
      <div className="br-auth-columns">
        <Media greaterThan="tablet">
          {(className, renderChildren) => renderChildren && <aside className="br-auth-pitch"><SalesPitch /></aside>}
        </Media>
        <div className="br-auth-panel">
          <nav className="br-auth-tabs" aria-label={t('Account')}>
            <Link to={`/signin${location.search}`} aria-current="page">{t('SignIn')}</Link>
            <Link to={`/signup${location.search}`}>{t('CreateAccount')}</Link>
          </nav>
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
          {message && <Alert variant="info">{message}</Alert>}
          {user === undefined && (
            <form onSubmit={onSubmit} aria-busy={signingIn} className="br-auth-form">
              {show === 'stage1' && <>
                <div className="br-auth-input-action" ref={emailInput as React.RefObject<HTMLDivElement>}>
                  <input className="br-field__input" type="email" autoComplete="email"
                    aria-label={t('Email')} placeholder={t('Email')} value={email}
                    onChange={emailChanged} />
                  <Button type="submit" aria-label={t('Continue')} disabled={signingIn}>
                    <AppIcon name="chevron right" aria-hidden="true" />
                  </Button>
                </div>
                <div className="br-auth-divider"><span>{t('OrUse')}</span></div>
                <div className="br-auth-social">
                  <Button variant="secondary" type="button" disabled={signingIn} onClick={() => signIn('facebook')}>
                    <span className="br-auth-provider-mark br-auth-provider-mark--facebook" aria-hidden="true">f</span>{t('ContinueWithFacebook')}
                  </Button>
                  <Button variant="secondary" type="button" disabled={signingIn} onClick={() => signIn('google')}>
                    <span className="br-auth-provider-mark br-auth-provider-mark--google" aria-hidden="true">G</span>{t('ContinueWithGoogle')}
                  </Button>
                  <Button variant="secondary" type="button" disabled={signingIn} onClick={() => signIn('apple')}>
                    <span className="br-auth-provider-mark" aria-hidden="true">●</span>{t('ContinueWithApple')}
                  </Button>
                </div>
              </>}
              {show === 'stage2' && <>
                <label htmlFor="signin-email">{t('Email')}</label>
                <input id="signin-email" className="br-field__input" type="email" autoComplete="email"
                  value={email} onChange={emailChanged} />
                <label htmlFor="signin-password">{t('Password')}</label>
                <div className="br-auth-input-action" ref={passwordInput as React.RefObject<HTMLDivElement>}>
                  <input id="signin-password" className="br-field__input" type="password" autoComplete="current-password"
                    value={password} placeholder={t('Password')}
                    onChange={event => passwordChanged(event, {value: event.currentTarget.value})} />
                  <Button type="submit" aria-label={t('SignIn')} aria-busy={loading === 'password'} disabled={Boolean(loading)}>
                    <AppIcon name="chevron right" aria-hidden="true" />
                  </Button>
                </div>
                <div className="br-auth-divider"><span>{t('Or').toUpperCase()}</span></div>
                <Button type="button" variant="secondary" aria-busy={loading === 'magic-link'}
                  disabled={Boolean(loading)} onClick={handleUseMagicLink}>
                  {t('SendMagicLinkToX', {x: email})}
                </Button>
              </>}
              {show === 'stage3' && <>
                <h2>Re-enter your email</h2>
                <div ref={emailInput as React.RefObject<HTMLDivElement>}>
                  <input className="br-field__input" type="email" autoComplete="email"
                    aria-label={t('Email')} placeholder={t('Email')} value={email} onChange={emailChanged} />
                </div>
                <Button type="submit">{t('Submit')}</Button>
              </>}
              <ForgotPasswordWidget />
            </form>
          )}
          {user !== undefined && profileLoadError ? (
            <Alert variant="error">
              <strong>Could not load your profile</strong>
              <p>The profile service returned {profileLoadError}. Please try again.</p>
              <div className="br-auth-actions">
                <Button type="button" onClick={retryProfile}>{t('Retry')}</Button>
                <Button type="button" variant="secondary" onClick={resetEverything}>{t('SignOut')}</Button>
              </div>
            </Alert>
          ) : (user !== undefined || verifyingMagicLink) && (
            <>
              <Alert variant="info">
                {user !== undefined ? t('Redirecting') : t('Verifying')}
                <AppIcon name="spinner" loading aria-hidden="true" />
              </Alert>
              <Alert variant="info">
                Is it taking too long?
                <div><Button type="button" variant="secondary" onClick={resetEverything}>{t('SignOut')}</Button></div>
              </Alert>
            </>
          )}
        </div>
        <Media at="tablet">
          {(className, renderChildren) => renderChildren && <aside className="br-auth-pitch"><SalesPitch /></aside>}
        </Media>
      </div>
    </main>
  );
};

export default SignIn;
